import { getUnitLabel, getUnitLabelForUnitSystem, getUnitCodeForUnitSystem } from '@icommerce/units';
import type { UnitSystem, UnitCode } from '@icommerce/units';
import type { AttributeDefinitionFragmentEnhanced } from '@models/enhanced-fragments';
import type { JSONLD_PRODUCT_ATTRIBUTES } from '@libs/constants';
import { stringToArray } from './utils';
import type { AttributeDefinitionFragment } from '@generated/graphql';

export type StrangeMapType = { map: { [locale: string]: string[] } };

export type ProductAttributeValueAmountUnit = {
  amount: number;
  unit?: UnitCode;
  calculations?: Record<UnitCode, number>;
};

export type ProductAttributeValue = boolean | string | ProductAttributeValueAmountUnit;

export type ProductAttributeUnion =
  | string
  | number
  | boolean
  | string[]
  | LabeledAttribute<string>
  | LabeledAttribute<number>
  | LabeledAttribute<boolean>
  | LabeledAttribute<string[]>
  | LabeledAttribute<ProductAttributeValueAmountUnit>
  | LabeledAttribute<StrangeMapType>;

export type ProductFragmentAttributes = Record<string, ProductAttribute<ProductAttributeUnion>>;

export type JsonLdProductAttributesKeys = (typeof JSONLD_PRODUCT_ATTRIBUTES)[number];
export type JsonLdProductFragmentAttributes = {
  [K in JsonLdProductAttributesKeys]?: ProductAttribute<ProductAttributeUnion>;
};

export type ProductAttribute<Value> = Value | LabeledAttribute<Value>;

export type LabeledAttribute<Value> = {
  label: string;
  value: Value;
  value_key?: string;
  attributeDefinition?: { label: string; type: { name: string } };
};

/**
 * Checks whether the given attribute value is a translation key. Meaning the value has to be
 * treated as a key to retrieve translated values from the i18n_properties table.
 */
export const isTranslatableAttributeValue = (attributeValue: ProductAttribute<ProductAttributeUnion>): boolean =>
  attributeValue != null && typeof attributeValue === 'string' && attributeValue.startsWith('attributes_');

export function isLabeledAttribute<T>(attribute: ProductAttribute<T>): attribute is LabeledAttribute<T> {
  return typeof attribute === 'object' && attribute != null && 'label' in attribute && 'value' in attribute;
}

export function getAttributeValue<ReturnType>(attribute: ProductAttribute<unknown>): ReturnType {
  const attributeValue = isLabeledAttribute<unknown>(attribute) ? attribute.value : attribute;

  if (
    typeof attributeValue === 'string' &&
    attributeValue.trim().startsWith('[') &&
    attributeValue.trim().endsWith(']')
  ) {
    try {
      return stringToArray(attributeValue) as ReturnType;
    } catch (error) {
      return [] as ReturnType;
    }
  }
  return attributeValue as ReturnType;
}

// used primarily for attributes that may contain links (e.g. "/info/7214/chainflex_classification")
export const getAttributeValueAsString = (attribute: ProductAttribute<string>) =>
  getAttributeValue<string>(attribute) || '';

// some attributes are linked to a reference entity that have multiple attributes (e.g. mounting_variant_icon on echain products)
// to retrieve these special values, we have to get the attribute definition and values and read the attribute from there
export function getAdditionalAttributeValue(
  attribute: ProductAttribute<LabeledAttribute<string>>,
  attributeDefinitions: AttributeDefinitionFragmentEnhanced[],
  attributeDefinitionKey: string,
  additionalAttributeKey: string
): string | undefined {
  // when there is an attribute definition for this attribute, check if it has a translated attribute value matching the value key,
  // if not, just use the value (or value.value) itself
  const attributeDefinition = attributeDefinitions?.find(item => item.attribute_id === attributeDefinitionKey);
  const attributeValue = attributeDefinition?.attribute_values?.find(item => item.value_key === attribute?.value_key);

  const additionalAttribute =
    attributeValue?.additional_values_translated &&
    attributeValue?.additional_values_translated[additionalAttributeKey];
  const additionalAttributeValue =
    additionalAttribute && isLabeledAttribute<string>(additionalAttribute)
      ? additionalAttribute.value
      : additionalAttribute;
  // previous implementation:  return additionalAttributeValue?.label || additionalAttributeValue;
  return additionalAttributeValue || undefined;
}

export const getAttributeValueAmount = (attribute: ProductAttributeValueAmountUnit, unitSystem: UnitSystem): number => {
  const unitCodeForUnitSystem = getUnitCodeForUnitSystem(attribute.unit, unitSystem);

  if (attribute.unit !== unitCodeForUnitSystem && attribute.calculations?.[unitCodeForUnitSystem]) {
    return attribute.calculations[unitCodeForUnitSystem];
  }

  return attribute.amount;
};

export const getAttributeValueAmountLabel = (
  attribute: ProductAttributeValueAmountUnit,
  unitSystem: UnitSystem,
  options?: { amountFormatter?: (amount: number) => string; showUnitLabel?: boolean }
): string => {
  const valueAmountForUnitSystem = getAttributeValueAmount(attribute, unitSystem);
  const amountFormatter = options?.amountFormatter || ((amount: number) => `${amount}`);
  let unitLabel = options?.showUnitLabel ? ` ${getUnitLabel(attribute.unit)}` : '';

  if (attribute.amount !== valueAmountForUnitSystem) {
    unitLabel = options?.showUnitLabel ? ` ${getUnitLabelForUnitSystem(attribute.unit, unitSystem)}` : '';

    return `${amountFormatter(valueAmountForUnitSystem)}${unitLabel} (${amountFormatter(
      attribute.amount
    )} ${getUnitLabel(attribute.unit)})`;
  }

  return `${amountFormatter(attribute.amount)}${unitLabel}`;
};

export const getAttributeDisplayValue = (
  unitSystem: UnitSystem,
  attributeValue: ProductAttributeValue,
  attributeDefinition?: AttributeDefinitionFragment
) => {
  if (typeof attributeValue !== 'object') {
    return typeof attributeValue === 'boolean' ? attributeValue : String(attributeValue);
  }

  if (attributeDefinition && attributeDefinition?.typename !== 'METRIC') {
    // Attribute type other than "METRIC" is not supported for object values.
    // "METRIC" in this case is not related to the unit system but to the attribute type
    // a proper naming would be "MEASUREMENT"
    return undefined;
  }

  if (!attributeValue.amount) {
    return undefined;
  }

  const isAttributeWeightMetric = attributeDefinition?.attribute_id === 'attr_weight_metric';
  if (isAttributeWeightMetric) {
    return getAttributeValueAmountLabel(attributeValue, unitSystem, {
      amountFormatter: (amount: number) => amount.toFixed(1),
      showUnitLabel: true,
    });
  }

  return getAttributeValueAmountLabel(attributeValue, unitSystem, { showUnitLabel: true });
};
